"use strict";

/**
 * Mix into the item model to get functions for image manipulation.
 * This is only helpful if you are saving item photo data. Not necessary for most item models since saving item
 * information does not save image data as well. Image data saves to a separate endpoint.
 */

const _ = require('underscore');
const assert = require('bunsen/helpers/assert');
const PhotosDataList = require('bunsen/models/inventory/PhotosDataList');
const Photo = require('bunsen/models/inventory/Photo');
const photoTypes = ['thumb', 'small', 'medium', 'large', 'original', 'master', 'zoom'];
let lastFetchImgData;

module.exports = {

    photoTypes,

    /**
     * Send save request to image (photos) endpoint. Uses the PhotosDataList object to do this saving logic.
     * @returns {*}
     */
    savePhotos() {
        let dfd;
        const dataList = new PhotosDataList({
            dataList : this.get('photos').toJSON()
        });

        dataList.setItemId(this.get('id'));
        dataList.removeInvalidPhotos();

        dfd = dataList.save({}, {
            type: 'PUT'
        });

        dfd.promise().then(() => {
            lastFetchImgData = this.get('photos').toJSON();
        });
        return dfd;
    },

    /**
    * Force the item model to update it's last fetched image data to the current state of the model.
     * This is a work-around for getting accurate data. Using the SOA Model's "lastSyncAttributes" may not be accurate
     * due to the order callbacks occur after sync. Similarly, Backbone's "changedAttributes" only takes into
     * consideration attributes that were changed since last set.
     * Invoke this method in your success callback before checkPhotosModified is called so that your data is being
     * compared against the correct version.
    */
    updateLastFetchImgData() {
        lastFetchImgData = this.toJSON().photos;
    },

    /**
     * Adds photo data to the item model based on a file upload.
     * Used for single and multi file uploads.
     * @param data <object|array> - returned photo data from image upload endpoint - or - an array of such objects.
     * @returns {boolean}
     */
    addPhotos(data) {
        let success = true;
        let photoCounter;
        const photos = this.get('photos');

        // normalize data
        if (!_.isArray(data)) {
            data = [data];
        }

        // fix current data on the item if there are any problems :
        this.removeInvalidPhotos();
        this.fixPhotoOrder();

        photoCounter = photos.length;

        data.forEach(photo => {
            if (!_.isObject(photo)) {
                this.triggerBubble('imageupload-multi-fail', this._messages.FAIL);
                success = false;
            }

            const photoData = _.pick(photo, ['format', 'status', 'workflowState', 'source', 'versions']);
            const newPhoto = new Photo(photoData);
            newPhoto.set('sortOrder', ++photoCounter);
            photos.add(newPhoto);
        });

        this.responseData = [];

        return success;
    },

    /**
     * Iterate over all photos and compare against the data from the last save. If any of the values are different
     * from what came back from the server on the last save or fetch then this will return true, else false.
     *
     * - Useful for the image upload page :
     * If these conditions are not met then we don't want to save the item. This is useful for distinguishing false
     * drop events on the thumbnail container view. ie. When the user clicks a thumbnail our 3rd party lib sometimes
     * misinterprets this as a drop event.
     * @returns {boolean}
     */
    checkPhotosModified() {
        const newPhotos = this.get('photos').toJSON();
        let modified = false;
        let atLeastOneValue = false;

        _.each(newPhotos, newPhoto => {
            const lastPhoto = _.findWhere(lastFetchImgData, {sortOrder : newPhoto.sortOrder});

            _.each(photoTypes, type => {
                // will return a list of matches but we just want first one
                const oldValList = lastPhoto ? _.filter(lastPhoto.versions, this._versionTypeFilter, {"type": type}) : [];
                const newValList = newPhoto ? _.filter(newPhoto.versions, this._versionTypeFilter, {"type": type}) : [];
                const oldVal = oldValList.length ? oldValList[0] : undefined;
                const newVal = newValList.length ? newValList[0] : undefined;

                if (newVal) {
                    atLeastOneValue = true;
                }

                if (newVal !== oldVal) {
                    modified = true;
                }
            });
        });

        // If there isn't at least 1 photo value, then don't save. This would happen in a case where no photos
        // had yet been added to the item.
        if (!atLeastOneValue) {
            return false;
        }

        return modified;
    },

    /**
     * Search JSON version data for version of specific type.
     * @param version - a single version object in JSON
     * @returns {boolean} - will be used as a predicate function with _.filter
     * @private
     */
    _versionTypeFilter(version) {
        if (version && version.type) {
            return version.type.name === this.type; // type passed in via context argument to _.filter
        }
    },

    /**
     * Fixes the order of the photos by removing models that do not have photos, applying the correct 'position' to
     * those that do (iteratively). Then add back all the photo models up to 10 and re-render this view.
     */
    fixPhotoOrder() {
        const photos = this.get('photos');
        let i;
        this.removeInvalidPhotos();
        let arr = [];


        // for any photos that have a sortOrder over 10, assign it to 10
        photos.each(photo => {
            if (parseInt(photo.get('sortOrder'), 10) > 10) {
                photo.set('sortOrder', 10);
            }
        });

        // iteratively find photos of incrementing indexes. Place them in order in the array "arr"
        for (i = 1; i <= 10; i++) {
            const photo = photos.where({'sortOrder' : i});
            if (photo.length) {
                arr = arr.concat(photo);
            }
        }

        // loop over "arr" assigning incrementing sortOrders to each.
        arr.forEach((val, idx) => {
            val.set('sortOrder', ++idx);
        });
    },

    /**
     * Adds in place-holder photo models for up to 10 spots on the item.
     */
    addPlaceHolderPhotos() {
        const photos = this.get('photos');
        let photo;
        let i;

        // Now loop up to 10, for any photos that are missing in this range, init an empty photo model :
        for (i = 1; i <= 10; i++) {
            photo = photos.findWhere({sortOrder : i});
            if (!photo) {
                photos.add(new Photo({
                    sortOrder : i
                }));
            }
        }
    },

    /**
     * Removes photos from the item that have no versions.
     */
    removeInvalidPhotos() {
        const photos = this.get('photos');
        const duds = [];

        photos.each(p => {
            if (!(p.get('versions') && p.get('versions').length)) {
                duds.push(p);
            }
        });
        photos.remove(duds);
    },

    /**
    * @deprecated - use item.getPhotos().at(0).download(options);
     * Retrieve a download link for a photo on the item.
     * @returns {Deferred}
     */
    downloadPhoto(options) {
        assert(options.photo, 'You must include the photo being downloaded.');
        return options.photo.download(options);
    }

};



// WEBPACK FOOTER //
// ./~/bunsen/models/mixins/ItemPhotosMixin.es.js